import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import ComingSoonPopup from './ComingSoonPopup';
import { IconMark, IconMenu, IconX } from './Icons';

interface HeaderProps {
  currentPage?: 'home' | 'pricing' | 'docs' | 'community' | 'other';
  showAuthButton?: boolean;
}

export default function Header({ currentPage = 'other', showAuthButton = true }: HeaderProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation(['common']);
  const isMobile = useIsMobile();
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const goLogin = () => navigate('/login');
  const goProfile = () => navigate('/profile');

  const NavLink = ({ k, label, href, onClick }: { k: HeaderProps['currentPage']; label: string; href?: string; onClick?: () => void }) => {
    const isActive = currentPage === k;
    if (href && !onClick) {
      return (
        <a href={href} className={'nav-link ' + (isActive ? 'active' : '')}>
          {label}
        </a>
      );
    }
    return (
      <button type="button" onClick={onClick} className={'nav-link ' + (isActive ? 'active' : '')}>
        {label}
      </button>
    );
  };

  const authButton = showAuthButton && (
    isAuthenticated ? (
      <button type="button" onClick={goProfile} className="btn sm">
        <span style={{ width: 18, height: 18, borderRadius: 999, background: 'var(--surface-4)', display: 'inline-block' }} />
        {t('common:navigation.profile', 'Profile')}
      </button>
    ) : (
      <button type="button" onClick={goLogin} className="btn primary sm">
        {t('common:navigation.getStarted', 'Start Building')}
      </button>
    )
  );

  return (
    <>
      <header className="site-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
          <a href="/" className="brand">
            <IconMark size={22} />
            {t('common:app.name', 'Entire')}
          </a>
          {!isMobile && (
            <nav className="site-nav">
              <NavLink k="home" label={t('common:navigation.home', 'Home')} href="/" />
              <NavLink k="community" label={t('common:navigation.community', 'Community')} onClick={() => setShowComingSoonPopup(true)} />
              <NavLink k="docs" label={t('common:navigation.docs', 'Docs')} href="/docs" />
              <NavLink k="pricing" label={t('common:navigation.pricing', 'Pricing')} href="/pricing" />
            </nav>
          )}
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {authButton}
            <button
              type="button"
              className="btn ghost sm icon"
              aria-label={t('common:navigation.menu', 'Menu')}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen((v) => !v)}
            >
              {mobileMenuOpen ? <IconX /> : <IconMenu />}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="chip">
              <span className="dot" style={{ background: 'var(--accent)' }} />
              v{__APP_VERSION__} · Beta
            </span>
            {authButton}
          </div>
        )}
      </header>

      {isMobile && mobileMenuOpen && (
        <nav
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            padding: '16px var(--gutter)',
            borderBottom: '1px solid var(--line)',
            background: 'var(--bg)',
          }}
        >
          <NavLink k="home" label={t('common:navigation.home', 'Home')} href="/" />
          <NavLink k="community" label={t('common:navigation.community', 'Community')} onClick={() => { setShowComingSoonPopup(true); setMobileMenuOpen(false); }} />
          <NavLink k="docs" label={t('common:navigation.docs', 'Docs')} href="/docs" />
          <NavLink k="pricing" label={t('common:navigation.pricing', 'Pricing')} href="/pricing" />
        </nav>
      )}

      <ComingSoonPopup
        isOpen={showComingSoonPopup}
        onClose={() => setShowComingSoonPopup(false)}
        title={t('comingSoon.community', 'Community')}
      />
    </>
  );
}
